import {
	IChartParams,
	IPointSizes,
	IPointWithAlignAndSize,
	AlignTypes,
	IChartPoint,
} from '../../models';
import {
	LABEL_HEIGHT_PX,
	LABEL_OFFSET_PX,
	POINT_RADIUS_PX,
	SUM_POINT_RADIUS_PX,
} from './chartConstants';
import { getLongestLabelString } from './chartFunctions';

export const getPointLabelCoordinates = (
	chartParams: IChartParams,
	point: IPointWithAlignAndSize,
	align: AlignTypes,
) => {
	const { xRatio, yRatio } = chartParams;
	const pointXRelativeRadius = POINT_RADIUS_PX * xRatio;
	const pointYRelativeRadius = POINT_RADIUS_PX * yRatio;
	const relativeXLabelOffset = LABEL_OFFSET_PX * xRatio;
	const relativeYLabelOffset = LABEL_OFFSET_PX * yRatio;

	const {
		x,
		y,
		relativeHalfLabelWidth,
		relativeLabelWidthWithOffset,
		relativeLabelHeightWithOffset,
		relativeHalfLabelHeight,
	} = point;

	switch (align) {
		case AlignTypes.bottom:
			return {
				labelLeftTopX: x - relativeHalfLabelWidth,
				labelLeftTopY: y - pointYRelativeRadius - relativeYLabelOffset,
			};
		case AlignTypes.top:
			return {
				labelLeftTopX: x - relativeHalfLabelWidth,
				labelLeftTopY: y + pointYRelativeRadius + relativeLabelHeightWithOffset,
			};
		case AlignTypes.right:
			return {
				labelLeftTopX: x + pointXRelativeRadius + relativeXLabelOffset,
				labelLeftTopY: y + relativeHalfLabelHeight,
			};
		case AlignTypes.left:
			return {
				labelLeftTopX: x - pointXRelativeRadius - relativeLabelWidthWithOffset,
				labelLeftTopY: y + relativeHalfLabelHeight,
			};
		case AlignTypes.center:
			return {
				labelLeftTopX: x - pointXRelativeRadius,
				labelLeftTopY: y + pointYRelativeRadius,
			};
		default:
			return {
				labelLeftTopX: x - pointXRelativeRadius,
				labelLeftTopY: y + pointYRelativeRadius,
			} as never;
	}
};

export const getPointCoordinates = (
	chartParams: IChartParams,
	point: IPointWithAlignAndSize,
) => {
	const { xRatio, yRatio } = chartParams;
	const pointXRelativeRadius = SUM_POINT_RADIUS_PX * xRatio;
	const pointYRelativeRadius = SUM_POINT_RADIUS_PX * yRatio;

	return {
		pointLeftTopX: point.x - pointXRelativeRadius,
		pointLeftTopY: point.y + pointYRelativeRadius,
	};
};

export const getPointLabelSizes = (
	point: IChartPoint,
	chartParams: IChartParams,
): IPointSizes => {
	const { chart, xRatio, yRatio } = chartParams;
	if (chart.ctx) {
		chart.ctx.font = "12px 'Roboto', sans-serif";
	}

	const labelString = getLongestLabelString(point.label);
	const labelWidthPX = chart.ctx?.measureText(labelString).width || 0;
	const relativeLabelWidth = labelWidthPX * xRatio;
	const relativeHalfLabelWidth = relativeLabelWidth / 2;
	const relativeLabelWidthWithOffset =
		(labelWidthPX + LABEL_OFFSET_PX) * xRatio;
	const relativeLabelHeight = LABEL_HEIGHT_PX * yRatio;
	const relativeHalfLabelHeight = relativeLabelHeight / 2;
	const relativeLabelHeightWithOffset =
		(LABEL_HEIGHT_PX + LABEL_OFFSET_PX) * yRatio;

	return {
		labelWidthPX,
		relativeLabelWidth,
		relativeHalfLabelWidth,
		relativeLabelWidthWithOffset,
		relativeLabelHeight,
		relativeHalfLabelHeight,
		relativeLabelHeightWithOffset,
	};
};
